import {IconButton, MetricDelta} from '@wandb/components';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React, {memo} from 'react';

import type {ValueOp} from '../../../util/filterTypes';
import {useRampFlagBaselineDeltaMetrics} from '../../../util/rampFeatureFlags';
import {displayValue, sortableValue} from '../../../util/runhelpers';
import type {Key as RunKey, Value as RunValue} from '../../../util/runTypes';
import {FilterUneditable} from '../../Filters/FilterUneditable';
import {useBaselineRunSummaryMetric} from '../RunSelectorBaselineRunContext';
import {calculateMetricDelta} from '../util';

type FilterTooltipProps = {
  columnKey: RunKey;
  cellValue: RunValue;
};
function FilterTooltip({columnKey, cellValue}: FilterTooltipProps) {
  return (
    <TailwindContents>
      <div className="flex flex-col gap-4">
        <div className="text-moon-400">Apply filter to table:</div>
        <div className="flex">
          <FilterUneditable
            filter={{
              key: columnKey,
              op: '=',
              value: cellValue,
            }}
          />
        </div>
      </div>
    </TailwindContents>
  );
}

interface WBTableCellDefaultProps {
  cellValue: RunValue;
  children?: React.ReactNode;
  columnKey: RunKey;
  isBaselineRun: boolean;
  isHoveringRow: boolean;
  isHoveringColumn: boolean;
  onAddFilter?: (key: RunKey, op: ValueOp, value: RunValue) => void;
}

const metricDeltaFormattingOptions: Intl.NumberFormatOptions = {
  style: 'decimal',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
  signDisplay: 'exceptZero',
};

const WBTableCellDefaultComp: React.FC<WBTableCellDefaultProps> = ({
  children,
  columnKey,
  cellValue,
  onAddFilter,
  isBaselineRun,
  isHoveringRow,
  isHoveringColumn,
}) => {
  const shouldShowDelta = useRampFlagBaselineDeltaMetrics();
  const delta = calculateMetricDelta(
    useBaselineRunSummaryMetric(columnKey),
    cellValue
  );

  // Using display here instead of conditional rendering to avoid expensive
  // rerendering. Mounting/unmounting for many cells is very costly in large
  // tables like large_runs_demo.
  const style = {display: isHoveringRow && isHoveringColumn ? 'flex' : 'none'};
  const addFilterControl = onAddFilter ? (
    <div className="flex flex-col justify-center" style={style}>
      <IconButton
        icon="filter-alt"
        aria-label="Add filter"
        size="sm"
        tooltip={{
          children: (
            <FilterTooltip cellValue={cellValue} columnKey={columnKey} />
          ),
        }}
        onClick={() => onAddFilter(columnKey, '=', sortableValue(cellValue))}
      />
    </div>
  ) : null;

  if (children) {
    return (
      <TailwindContents>
        {children}
        {addFilterControl}
      </TailwindContents>
    );
  }

  const baselineDelta =
    !isBaselineRun && delta != null && shouldShowDelta ? (
      <div className="flex flex-col justify-center items-end grow shrink">
        <MetricDelta
          value={delta}
          tone="neutral"
          shade={2}
          formatting={metricDeltaFormattingOptions}
        />
      </div>
    ) : null;

  return (
    <TailwindContents>
      <div className="shrink-0 overflow-hidden">{displayValue(cellValue)}</div>
      {addFilterControl}
      {baselineDelta}
    </TailwindContents>
  );
};

export const WBTableCellDefault = memo(WBTableCellDefaultComp);
